import { defineStore } from 'pinia'

const LSKEY_SIGNIN_ON = 'jps-signin-feature'

/**
 * ユーザー情報を管理するためのStateStore。ログインは管理画面でしか使わないため、SSR側では常にログアウト状態とする（管理画面はClientOnlyで作る）。
 */
export const useUserStore = defineStore(
  'user',
  () => {
    const _uao = ref<UaO>(null)

    const setUaO = (uao: UaO) => {
      _uao.value = uao
    }

    const userRole = computed(() => {
      return _uao.value?.role
    })

    const isOrg = computed(() => {
      return _uao.value?.orgId != null
    })

    const isLogin = computed(() => {
      return _uao.value?.user != null
    })

    const isProject = computed(() => {
      return _uao.value?.orgType === 'PROJECT'
    })

    const isSysad = computed(() => {
      return isLogin.value && userRole.value === 'SYSTEM_ADMIN'
    })

    const isAdmin = computed(() => {
      return isLogin.value && ['ADMIN', 'SYSTEM_ADMIN'].includes(userRole.value)
    })

    const isEditor = computed(() => {
      return (
        isLogin.value &&
        ['ADMIN', 'SYSTEM_ADMIN', 'EDITOR'].includes(userRole.value)
      )
    })

    const hasEditForData = data => {
      return (
        isSysad.value ||
        (isEditor.value &&
          _uao.value.orgId === (<Permission>data.permission).ownerOrg)
      )
    }

    const hasAdminFor = data => {
      if (data) return (<Permission>data.permission).level === 'ADMIN'
      return false
    }

    const getOrgId = computed(() =>{
      return _uao.value?.orgId
    })

    const getLoginUserId = computed(() => {
      return _uao.value?.user?.id
    })

    const isEmailConfirmed = computed(() => {
      return _uao.value?.user?.emailConfirmed
    })

    const isSigninOn = computed<boolean>({
      get: () => lsGet(LSKEY_SIGNIN_ON, false),
      set: v => lsSet(LSKEY_SIGNIN_ON, v),
    })

    /**
     * サインインすると強制的に管理画面にリダイレクトする.UaAは/api/page/pathの応答でセットされる
     */
    async function signin(id: string, password: string) {
      const session = useSession()
      try {
        const result = await session.signin(id, password)
        if (!result) return false
        if (!isSigninOn.value) {
          isSigninOn.value = true
        }
        return true
      } catch (e) {
        throw e
      } finally {
      }
    }

    async function signout() {
      const session = useSession()
      try {
        await session.signout()
        _uao.value = null
        return true
      } catch (e) {
        _uao.value = null
        return false
      } finally {
      }
    }

    async function switchAccount(organizationId: string) {
      const { uaoSwitch } = useSession()
      try {
        _uao.value = await uaoSwitch(organizationId)
      } catch (e) {
        _uao.value = null
        return false
      } finally {
      }
    }

    return {
      isLogin,
      userRole,
      isOrg,
      isProject,
      isSysad,
      isAdmin,
      isEditor,
      hasEditForData,
      hasAdminFor,
      getOrgId,
      getLoginUserId,
      isEmailConfirmed,
      isSigninOn,
      setUaO,
      signin,
      signout,
      switchAccount,
    }
  },
  { persist: false },
)
